
import { CrystalBallFormData, TimeOff, CrystalBallCalculations, Scenario } from './types';

export const calculateWorkDaysAndHours = (timeOff: TimeOff) => {
  const vacation = parseInt(timeOff.vacation) || 0;
  const sick = parseInt(timeOff.sick) || 0;
  const holidays = parseInt(timeOff.holidays) || 0;
  
  const availableWorkDays = 260 - (vacation + sick + holidays); // 52 weeks * 5 days - time off
  const availableWorkHours = availableWorkDays * 8;
  
  return { availableWorkDays, availableWorkHours };
};

export const calculateCrystalBall = (formData: CrystalBallFormData): CrystalBallCalculations => {
  const hourlyRate = parseFloat(formData.desiredHourlyRate) || 0;
  const projectHours = parseFloat(formData.projectHours) || 0;
  const complexityMultiplier = parseFloat(formData.complexityFactor) || 1.0;
  const billableHoursPerDay = parseFloat(formData.billableHoursPerDay) || 6;

  // Basic rate calculations
  const dailyRate = hourlyRate * billableHoursPerDay;
  const weeklyRate = dailyRate * 5;

  // Project calculations with safety checks
  const averageProjectFee = Math.max(0, projectHours * hourlyRate);
  const adjustedProjectFee = averageProjectFee * complexityMultiplier;

  // Calculate total expenses
  const monthlyExpenses = Object.values(formData.expenses).reduce(
    (sum, expense) => sum + (parseFloat(expense) || 0),
    0
  );
  const yearlyExpenses = monthlyExpenses * 12;
  
  // Time calculations
  const { availableWorkDays, availableWorkHours } = calculateWorkDaysAndHours(formData.timeOff);
  const maxProjects = Math.floor((availableWorkDays * billableHoursPerDay) / Math.max(1, projectHours));

  // Break-even calculation with safety check
  const breakEvenProjects = Math.ceil(yearlyExpenses / Math.max(1, adjustedProjectFee));

  // Scenario calculations with specified capacities
  const calculateScenario = (capacity: number): Scenario => {
    const projects = Math.floor(maxProjects * capacity);
    const monthlyProjects = Math.round((projects / 12) * 10) / 10;
    const income = projects * adjustedProjectFee;
    const monthlyIncome = income / 12;
    const workingHours = projects * projectHours;
    const utilizationRate = (workingHours / Math.max(1, availableWorkHours)) * 100;
    
    return {
      capacity,
      projects,
      projectsPerMonth: monthlyProjects,
      income,
      monthlyIncome,
      workingHours,
      utilizationRate,
      averageProjectFee: adjustedProjectFee
    };
  };

  return {
    dailyRate,
    weeklyRate,
    averageProjectFee,
    adjustedProjectFee,
    monthlyExpenses,
    yearlyExpenses,
    breakEvenProjects,
    availableWorkDays,
    availableWorkHours,
    maxProjects,
    scenarios: {
      minimum: calculateScenario(0.7),
      comfortable: calculateScenario(0.85),
      maximum: calculateScenario(1.0)
    }
  };
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

